/**
 * Product Gallery Nav - Scroll Progress Tracker
 * Updates pointer height based on scroll position
 */

export class ProductGalleryNav {
  private container: HTMLElement | null;
  private track: HTMLElement | null;
  private pointer: HTMLElement | null;
  private isScrolling = false;
  private isDesktop = false;

  constructor(galleryElement: HTMLElement) {
    this.container = galleryElement.querySelector('[data-product-gallery-container]');
    this.track = galleryElement.querySelector('[data-product-gallery-nav-track]');
    this.pointer = galleryElement.querySelector('[data-product-gallery-nav-pointer]');
    
    this.checkIsDesktop();
    
    if (this.container && this.track && this.pointer && this.isDesktop) {
      this.init();
    }

    // Update on resize
    window.addEventListener('resize', this.handleResize);
  }

  private checkIsDesktop(): void {
    this.isDesktop = window.innerWidth >= 1000;
  }

  private handleResize = (): void => {
    const wasDesktop = this.isDesktop;
    this.checkIsDesktop();
    
    if (wasDesktop !== this.isDesktop) {
      if (this.isDesktop) {
        this.init();
      } else {
        this.unbind();
      }
    }
  };

  private init(): void {
    if (!this.container) return;

    // Update on scroll
    this.container.addEventListener('scroll', this.handleScroll);
    
    // Initial update
    this.updatePointer();
  }

  private unbind(): void {
    if (this.container) {
      this.container.removeEventListener('scroll', this.handleScroll);
    }
  }

  private handleScroll = (): void => {
    if (!this.isScrolling) {
      this.isScrolling = true;
      requestAnimationFrame(() => {
        this.updatePointer();
        this.isScrolling = false;
      });
    }
  };

  private updatePointer(): void {
    if (!this.container || !this.track || !this.pointer || !this.isDesktop) return;

    // Calculate scroll progress (0 to 1)
    const scrollTop = this.container.scrollTop;
    const scrollHeight = this.container.scrollHeight;
    const clientHeight = this.container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    
    const scrollProgress = maxScroll > 0 ? scrollTop / maxScroll : 0;
    
    // Get track height
    const trackHeight = this.track.offsetHeight;
    
    // Calculate pointer height with 10px minimum
    const minHeight = 12;
    const calculatedHeight = trackHeight * scrollProgress;
    const pointerHeight = Math.max(minHeight, calculatedHeight);
    
    // Update pointer height
    this.pointer.style.height = `${pointerHeight}px`;
  }

  public destroy(): void {
    this.unbind();
    window.removeEventListener('resize', this.handleResize);
  }
}
